import type { BillingPlanUpgradeSource } from "@editor/reducers/api-reducer";
import type { V1PlanInfo } from "schemas/billing";
import type {
  BillingInterval,
  SubscriptionDetails,
  V1BillingTier,
} from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import * as React from "react";

import { useModal } from "@editor/hooks/useModal";
import { workspaceRouter } from "@editor/trpc/workspace";

import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { formatPrice } from "replo-utils/lib/math";
import { pluralize } from "replo-utils/lib/pluralize";
import {
  getV1BillingPlanInfoByTier,
  maxAdditionalPublishedElementBundles,
  v1AdditionalPublishedElementBundlePricePerMonth,
  v1AdditionalPublishedElementBundleSize,
  v1AdditionalPublishedElementsPerAdditionalShop,
  v1AdditionalShopPricePerMonth,
} from "schemas/billing";

import PlanCardButton from "../shared/PlanCardButton";
import QuantityControl from "../shared/QuantityControl";
import {
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfV1AdditionalShopsInSubscription,
  getPlanColorTheme,
  getPlanIcon,
} from "./v1BillingUtils";
import { V1PlanPricePerMonthIncludingAllProducts } from "./V1PlanPrice";

type V1PlanCardProps = {
  plan: V1PlanInfo;
  currentPlan: V1BillingTier;
  isYearly: boolean;
  workspace: Workspace;
  source: BillingPlanUpgradeSource;
  cardPlanTier: V1BillingTier;
  isDowngrade: boolean;
  isUpgrade: boolean;
  isCurrentTier: boolean;
  isYearlyIntervalUpgrade: boolean;
  isCustomPlan: boolean;
  billingInterval: BillingInterval;
  subscriptionDetails?: SubscriptionDetails;
  isSubscriptionDetailsPending: boolean;
};

const V1PlanCard: React.FC<V1PlanCardProps> = ({
  plan,
  currentPlan,
  isYearly,
  workspace,
  source,
  cardPlanTier,
  isDowngrade,
  isUpgrade,
  isCurrentTier,
  isYearlyIntervalUpgrade,
  isCustomPlan,
  billingInterval,
  subscriptionDetails,
  isSubscriptionDetailsPending,
}) => {
  const modal = useModal();

  const currentAdditionalShops = React.useMemo(() => {
    if (!subscriptionDetails) {
      return 0;
    }

    return getCountOfV1AdditionalShopsInSubscription(subscriptionDetails);
  }, [subscriptionDetails]);

  const currentAdditionalPublishedElementBundles = React.useMemo(() => {
    if (!subscriptionDetails) {
      return 0;
    }

    // NOTE (Cole, 2025-09-03): Basic plan is the only plan that can have published element bundles
    if (plan.tier !== "basic") {
      return 0;
    }

    return getCountOfAdditionalPublishedElementBundlesInSubscription(
      subscriptionDetails,
    );
  }, [subscriptionDetails, plan.tier]);

  const [
    additionalPublishedElementBundles,
    setAdditionalPublishedElementBundles,
  ] = React.useState<number>(currentAdditionalPublishedElementBundles);
  const [additionalShops, setAdditionalShops] = React.useState(
    currentAdditionalShops,
  );

  const totalShops = additionalShops + 1;

  const publishedElementsProvidedByShops =
    (plan.maxPublishedElements ?? 0) * totalShops;

  // Use local state for UI display, not subscription data
  const totalAdditionalPublishedElements =
    additionalPublishedElementBundles * v1AdditionalPublishedElementBundleSize;

  const handlePublishedElementsIncrement = () => {
    setAdditionalPublishedElementBundles((prev) =>
      Math.min(prev + 1, maxAdditionalPublishedElementBundles),
    );
  };

  const handlePublishedElementsDecrement = () => {
    setAdditionalPublishedElementBundles((prev) => Math.max(0, prev - 1));
  };

  const handleShopsIncrement = () => {
    setAdditionalShops((prev) => prev + 1);
  };

  const handleShopsDecrement = () => {
    setAdditionalShops((prev) => Math.max(0, prev - 1));
  };

  const colors = getPlanColorTheme(cardPlanTier);

  const hasConfigurationChanges =
    additionalPublishedElementBundles !==
      currentAdditionalPublishedElementBundles ||
    additionalShops !== currentAdditionalShops ||
    isYearlyIntervalUpgrade;

  // Note (Evan, 2024-03-26): There are 3 cases to account for here:
  // 1) If there's an active Stripe subscription, redirect to the billing plan confirmation modal
  // 2) If there's no active Stripe subscription and we're downgrading, redirect to the survey modal
  // 3) If there's no active Stripe subsbscription and we're upgrading, initiate the update immediately
  // (this is because Shopify (for upgrades) and Stripe (for new subscriptions) require confirmation already)
  const handleSubmitPlanChanges = () => {
    // NOTE (Cole 2025-09-11): We must make sure that if there's an existing subscription, it's loaded before we submit an update.
    // Otherwise there's a risk that we accidentally change the v2 plan as we change the v1 plan
    if (!workspace || isSubscriptionDetailsPending) {
      return;
    }

    if (isCustomPlan) {
      window.open("https://replo.app/contact", "_blank");
      return;
    }

    if (currentPlan === "custom" && isDowngrade) {
      modal.openModal({
        type: "billingModal",
        props: { source: "direct.billingDashboard" },
      });
      return;
    }

    const isCurrentInterval =
      subscriptionDetails?.billingInterval === billingInterval;

    if (isCurrentTier && isCurrentInterval && !hasConfigurationChanges) {
      return;
    }

    let billingUpgradeDowngradeModalType:
      | "upgrade"
      | "downgrade"
      | "update-configuration";
    if (isDowngrade) {
      billingUpgradeDowngradeModalType = "downgrade";
    } else if (isUpgrade) {
      billingUpgradeDowngradeModalType = "upgrade";
    } else {
      billingUpgradeDowngradeModalType = "update-configuration";
    }

    modal.closeModal({ type: "billingModal" });
    modal.openModal({
      type: "billingUpgradeDowngradeModal",
      props: {
        workspaceId: workspace.id,
        currentTier: currentPlan,
        newTier: cardPlanTier,
        type: billingUpgradeDowngradeModalType,
        source,
        newBillingInterval: billingInterval,
        newV1AdditionalShops: additionalShops,
        newAdditionalPublishedElementBundles: additionalPublishedElementBundles,
      },
    });
  };

  const totalPublishedElements =
    publishedElementsProvidedByShops + totalAdditionalPublishedElements;

  // Get current usage data
  const { data: publishedElementsUsage } =
    workspaceRouter.getPublishedElementsUsage.useQuery(
      !workspace?.id || isCustomPlan ? skipToken : workspace.id,
    );

  const { data: shopifyIntegrationsUsage } =
    workspaceRouter.getShopifyIntegrationsUsage.useQuery(
      !workspace?.id || isCustomPlan ? skipToken : workspace.id,
    );

  const isPlanConfigurationValidWithTooltipContent = React.useMemo(() => {
    if (isCustomPlan || !publishedElementsUsage || !shopifyIntegrationsUsage) {
      return {
        isPlanConfigurationValid: true,
        tooltipContent: undefined,
      };
    }

    const planInfo = getV1BillingPlanInfoByTier(cardPlanTier);

    const corePlanMaxShopifyIntegrations = planInfo.maxShopifyIntegrations;
    if (corePlanMaxShopifyIntegrations !== null) {
      const totalShopifyIntegrationsAllowed =
        corePlanMaxShopifyIntegrations + additionalShops;
      if (shopifyIntegrationsUsage.current > totalShopifyIntegrationsAllowed) {
        return {
          isPlanConfigurationValid: false,
          tooltipContent: `You have ${shopifyIntegrationsUsage.current} Shopify integrations, but your current plan configuration only allows ${totalShopifyIntegrationsAllowed} integrations. You'll need to add more Shopify stores to continue.`,
        };
      }
    }

    const corePlanMaxPublishedElements = planInfo.maxPublishedElements;
    if (corePlanMaxPublishedElements !== null) {
      const publishedElementsFromBundles =
        additionalPublishedElementBundles *
        v1AdditionalPublishedElementBundleSize;
      const publishedElementsFromShops =
        additionalShops * v1AdditionalPublishedElementsPerAdditionalShop;
      const totalPublishedElementsAllowed =
        corePlanMaxPublishedElements +
        publishedElementsFromBundles +
        publishedElementsFromShops;

      if (publishedElementsUsage.current > totalPublishedElementsAllowed) {
        return {
          isPlanConfigurationValid: false,
          tooltipContent: `You have ${publishedElementsUsage.current} published items, but your current plan configuration only allows ${totalPublishedElementsAllowed}. You'll need to add more published items or upgrade your plan to continue.`,
        };
      }
    }

    return {
      isPlanConfigurationValid: true,
      tooltipContent: undefined,
    };
  }, [
    isCustomPlan,
    publishedElementsUsage,
    shopifyIntegrationsUsage,
    cardPlanTier,
    additionalShops,
    additionalPublishedElementBundles,
  ]);

  if (!isCustomPlan && (!publishedElementsUsage || !shopifyIntegrationsUsage)) {
    return null;
  }

  const {
    isPlanConfigurationValid,
    tooltipContent: planConfigurationTooltipContent,
  } = isPlanConfigurationValidWithTooltipContent;

  return (
    <div
      className={twMerge(
        "rounded-lg border",
        "pt-4 pb-5 px-5 flex flex-col min-h-[458px] h-full overflow-hidden relative",
        colors.borderColor,
        colors.cardBg,
      )}
    >
      <div className="mb-2 mt-2 flex items-center gap-2">
        {getPlanIcon(
          cardPlanTier,
          colors.stroke,
          `flex items-center justify-center w-10 h-10 rounded-full border border-secondary bg-white shadow-sm`,
        )}
      </div>

      <div className="flex flex-col gap-1 mb-3">
        <h3 className="text-lg font-semibold">{plan.displayName}</h3>
        <p className="text-sm text-muted overflow-hidden text-ellipsis line-clamp-3">
          {plan.description}
        </p>
      </div>

      {cardPlanTier !== "custom" ? (
        <div className="flex flex-col gap-2 mb-3">
          <div className="text-2xl font-bold flex flex-wrap items-baseline">
            <V1PlanPricePerMonthIncludingAllProducts
              plan={plan}
              billingInterval={billingInterval}
              additionalShops={additionalShops}
              additionalPublishedElementBundles={
                additionalPublishedElementBundles
              }
              variant="header"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-sm text-muted">
              Billed {isYearly ? "annually" : "monthly"}
            </span>
            <Tag
              size="sm"
              variant="neutral"
              UNSAFE_className={twMerge(
                colors.bg,
                colors.text,
                colors.borderColor,
                "whitespace-nowrap border-1",
                !isYearly && "opacity-0",
              )}
            >
              Save 17%
            </Tag>
          </div>

          <QuantityControl
            value={additionalShops}
            onIncrement={handleShopsIncrement}
            onDecrement={handleShopsDecrement}
            label={
              <span>
                <span className="font-bold">{totalShops}</span> Shopify{" "}
                {pluralize({
                  singular: "store",
                  plural: "stores",
                  count: totalShops,
                })}{" "}
                {additionalShops > 0 && (
                  <>
                    (
                    <span className="font-bold">
                      +
                      {formatPrice(
                        additionalShops * v1AdditionalShopPricePerMonth,
                      )}
                    </span>
                    )
                  </>
                )}
              </span>
            }
            minValue={0}
            isViewOnly={false}
          />

          {plan.tier === "basic" ? (
            <QuantityControl
              value={additionalPublishedElementBundles}
              onIncrement={handlePublishedElementsIncrement}
              onDecrement={handlePublishedElementsDecrement}
              label={
                <span>
                  <span className="font-bold">{totalPublishedElements}</span>{" "}
                  published items{" "}
                  {additionalPublishedElementBundles > 0 && (
                    <>
                      (
                      <span className="font-bold">
                        +
                        {formatPrice(
                          additionalPublishedElementBundles *
                            v1AdditionalPublishedElementBundlePricePerMonth,
                        )}
                      </span>
                      )
                    </>
                  )}
                </span>
              }
              minValue={0}
              maxValue={maxAdditionalPublishedElementBundles}
              isViewOnly={false}
            />
          ) : (
            <div className="h-8" />
          )}
        </div>
      ) : (
        <div className="text-2xl font-bold mb-10 h-[108px]">Need more?</div>
      )}

      <ul className="flex-1 space-y-2 mb-6 overflow-y-auto">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <Check
              size={16}
              className={twMerge(colors.stroke, "shrink-0 mt-0.5")}
              strokeWidth={2}
            />
            {cardPlanTier !== "custom" ? (
              <span className="overflow-hidden text-ellipsis">
                {(() => {
                  const words = feature.split(" ");
                  const firstWord = words[0];

                  if (firstWord === "24/7") {
                    return feature;
                  }

                  return (
                    <>
                      <strong>{firstWord}</strong>
                      {words.length > 1 ? ` ${words.slice(1).join(" ")}` : ""}
                    </>
                  );
                })()}
              </span>
            ) : (
              <span className="overflow-hidden text-ellipsis">{feature}</span>
            )}
          </li>
        ))}
      </ul>
      <PlanCardButton
        isCurrentTier={isCurrentTier}
        hasConfigurationChanges={hasConfigurationChanges}
        isPlanConfigurationValid={isPlanConfigurationValid}
        invalidPlanConfigurationTooltipContent={planConfigurationTooltipContent}
        isCustomPlan={isCustomPlan}
        isYearlyIntervalUpgrade={isYearlyIntervalUpgrade}
        isDowngrade={isDowngrade}
        currentPlan={currentPlan}
        handleSubmitPlanChanges={handleSubmitPlanChanges}
        isManagedByReplo={subscriptionDetails?.isManagedByReplo ?? false}
        billingInterval={billingInterval}
        workspace={workspace}
        isSubscriptionDetailsPending={isSubscriptionDetailsPending}
        isPlanCancelledButNotYetEnded={
          subscriptionDetails?.endedAt != null &&
          subscriptionDetails.endedAt > new Date()
        }
      />
    </div>
  );
};

export default V1PlanCard;
